/**
 * Duplicate Closure Bill Config (accounting domain)
 *
 * Covers ``ex-register-closure-duplicate-bill``: ACCREC sales invoices
 * and ACCPAY COGS bills that appear more than once for the same
 * register closure (same fp + contact + closure date + reference +
 * origin type). The tagger picks the earliest as the "original" and
 * tags each later sibling - one row per duplicate.
 *
 * Companion view to the existing ``ex-register-closure-missing-in-xero``
 * exception. Reuses the platform-context columns. Detail comes straight
 * off ``franchise_exception_tags.metadata``.
 */
import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, datePart, textPart } from '../shared/rowSubtitle'

const renderTypeChip = (value: any): React.ReactNode => {
  if (!value) return '-'
  const isCogs = value === 'COGS Bill'
  return React.createElement(Chip, {
    label: value,
    color: isCogs ? 'warning' : 'info',
    size: 'small',
    variant: 'outlined',
  })
}

const renderLineItemsMatch = (value: any): React.ReactNode => {
  if (value === null || value === undefined) {
    return withTooltip(
      { title: 'No line items on either invoice' },
      React.createElement('span', null, '-')
    )
  }
  return React.createElement(Chip, {
    label: value ? 'Yes' : 'No',
    color: value ? 'success' : 'error',
    size: 'small',
  })
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'closure_date',
    label: 'Closure Date',
    type: 'date',
    contexts: ['inspector'],
  },
  {
    key: 'type_label',
    label: 'Type',
    align: 'center',
    contexts: ['inspector'],
    render: renderTypeChip,
  },
  {
    key: 'contact_name',
    label: 'Contact',
    contexts: ['inspector'],
  },
  {
    key: 'register_name',
    label: 'Register',
    contexts: ['inspector'],
    render: (value) => value || '-',
  },
  {
    key: 'reference',
    label: 'Reference',
    contexts: ['inspector'],
    render: (value) => value || '-',
  },
  {
    key: 'original_invoice_number',
    label: 'Original #',
    contexts: ['inspector'],
  },
  {
    key: 'original_total',
    label: 'Original Total',
    type: 'currency',
    align: 'right',
    contexts: ['inspector'],
  },
  {
    key: 'duplicate_invoice_number',
    label: 'Duplicate #',
    contexts: ['inspector'],
  },
  {
    key: 'duplicate_total',
    label: 'Duplicate Total',
    type: 'currency',
    align: 'right',
    contexts: ['inspector'],
  },
  {
    key: 'line_items_match',
    label: 'Line Items Match',
    align: 'center',
    contexts: ['inspector'],
    render: renderLineItemsMatch,
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'closure_date',
    label: 'Closure Date',
    type: 'date',
    contexts: ['platform'],
  },
  {
    key: 'type_label',
    label: 'Type',
    contexts: ['platform'],
  },
  {
    key: 'contact_name',
    label: 'Contact',
    contexts: ['platform'],
  },
  {
    key: 'register_name',
    label: 'Register',
    contexts: ['platform'],
  },
  {
    key: 'reference',
    label: 'Reference',
    contexts: ['platform'],
  },
  {
    key: 'original_invoice_number',
    label: 'Original #',
    contexts: ['platform'],
  },
  {
    key: 'original_total',
    label: 'Original Total',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'duplicate_invoice_number',
    label: 'Duplicate #',
    contexts: ['platform'],
  },
  {
    key: 'duplicate_total',
    label: 'Duplicate Total',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
  },
  {
    key: 'line_items_match',
    label: 'Line Items Match',
    align: 'center',
    contexts: ['platform'],
    render: (value) =>
      value === null || value === undefined ? '-' : value ? 'Yes' : 'No',
  },
]

export const duplicateClosureBillConfig: ExceptionConfig = {
  codes: ['ex-register-closure-duplicate-bill'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'closure_date',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'object_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.outlet_name),
      textPart(row.register_name),
      row.closure_date ? `closed ${datePart(row.closure_date)}` : null,
      typeof row.duplicate_count === 'number' && row.duplicate_count > 0
        ? `${row.duplicate_count} duplicates`
        : null,
    ]),
}
